import { ISO_DATE_FORMAT, formatApiDate } from '~/shared/lib/date'
import type { FC } from 'react'
import { DateAndRelativeDateInput, FFInput, PriorityToggle } from '~/shared/ui'
import {
  useGroupOptions,
  UserGroupSelect,
  type AssigneeGroupListItem,
} from '~/entities/assignee-group'
import { Box, InputAdornment } from '@mui/material'
import { memo } from 'react'
import { useSelector } from 'react-redux'
import { CarePlanModalTask } from './useCarePlanModal'
import type { PatientUser } from '~/shared/types'

const GRID_CELL_SX = { display: 'flex', pl: 1, mt: 1, flex: 1, minWidth: 275 }

interface CarePlanTaskEditorProps {
  task: CarePlanModalTask
  onChange: (task: CarePlanModalTask) => void
  disabled?: boolean
  patient?: PatientUser
}

/**
 * Task editor for CarePlansModal using snake_case properties directly.
 */
const CarePlanTaskEditor: FC<CarePlanTaskEditorProps> = props => {
  const { task, disabled = false, patient } = props
  const providers = useSelector(state => state.providers)
  const assigneeGroups = useSelector(state => state.assigneeGroups)
  let isTaskAssigneeGroupProvider: AssigneeGroupListItem | undefined = providers.find(
    provider => provider.assignee_group?.id === task.owner_group,
  )?.assignee_group
  isTaskAssigneeGroupProvider ??= assigneeGroups.find(group => group.id === task.owner_group)

  const enablePriorityToggle = isTaskAssigneeGroupProvider

  const { selectedCaseOwnerGroupOption, flatOptions, groupedOptions } = useGroupOptions(
    task.owner_group ?? null,
    true,
    patient,
    patient?.person?.care_team,
    disabled,
  )

  return (
    <div>
      <div>
        <FFInput
          placeholder="Task Title"
          value={task.title}
          fullWidth
          multiline
          onChange={e => props.onChange({ ...props.task, title: e.target.value })}
          disabled={disabled}
          slotProps={{
            htmlInput: {
              maxLength: 255,
            },
            input: {
              endAdornment: (
                <InputAdornment
                  color=""
                  position="end"
                >{`${task.title.length}/255`}</InputAdornment>
              ),
            },
          }}
        />
      </div>
      <Box sx={{ display: 'flex', ml: -1, flexWrap: 'wrap' }}>
        <Box sx={GRID_CELL_SX}>
          <DateAndRelativeDateInput
            split
            value={formatApiDate(task.due_date, ISO_DATE_FORMAT) ?? ''}
            onChange={due_date => props.onChange({ ...props.task, due_date })}
            disabled={disabled}
          />
        </Box>
        <Box sx={GRID_CELL_SX}>
          <UserGroupSelect
            selectedValue={selectedCaseOwnerGroupOption ?? null}
            options={flatOptions}
            groupedOptions={groupedOptions}
            onChange={ownerGroupOption => {
              const owner_group = ownerGroupOption.value
              if (owner_group) props.onChange({ ...props.task, owner_group })
            }}
            patient={patient}
            fullWidth
            disabled={props.disabled}
          />
          {enablePriorityToggle ? (
            <PriorityToggle
              priority={task.priority}
              onChange={priority => props.onChange({ ...props.task, priority })}
            />
          ) : null}
        </Box>
      </Box>
    </div>
  )
}

export default memo(CarePlanTaskEditor)
